import { takeLatest, put, select, all, call, cancelled, takeEvery } from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash/get';
import endsWith from 'lodash/endsWith';
import * as commonTypes from '@store/common/types';
import * as metricsChartContainerTypes from '@store/metricsChartContainer/types';
import http from '@api/http';
import { getDimensionData } from '@api/dimensionContainer';
import commonSelector, {
  horizontalLayoutSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import dimensionListSelector, {
  dimensionNamesByMetricSelector,
} from '@store/common/dimensionList/selectors';
import featureNames from '@utils/featureToggle/constants';
import { isErrorCodeByDimension } from '@utils/dimension';
import { METRICS_MAPPING, calcShortcutInterval } from '@utils';
import { selector as dimensionTablesSelector } from '.';
import * as types from './types';
import * as birdChartTypes from '../common/birdChart/types';
import {
  setQueryByDimensionName,
  createQueryByDimensionName,
  viewerDrillDownInterval,
} from './helper';
import selectors from './selectors';
import { createDimension } from './schema';
import * as viewerDrillDownBirdChartTypes from '../trends/birdChart/types';
import { setDimensionTableHeaderInterval } from '@store/dimensionTables/actions';
import shortId from '@utils/shortId';
import { noBrushSelector } from '@store/trends/birdChart/selectors';

function* fetchingDimensionData(action) {
  // yield put(setDimensionTableHeaderInterval(null));
  const dimensionTablesStore = yield select(dimensionTablesSelector);
  const { features } = yield select(commonSelector);
  const {
    [featureNames.ENUMERATED_VALUES_PER_DIMENSION]: limit,
    [featureNames.ERROR_CODES_LIMIT]: errorLimit,
  } = features;
  const { dimensions } = dimensionTablesStore;
  const { dimensionName } = action;
  const query = get(dimensions, `${dimensionName}.query`);
  if (!query) {
    return;
  }
  // Side effects for viewer drill down
  yield viewerDrillDownInterval(query);
  query.limit = isErrorCodeByDimension(action.dimensionName) ? errorLimit : limit;

  const realTimeMode = yield select(realTimeModeOnTrendsSelector);
  const noBrushSelected = yield select(noBrushSelector);

  if (realTimeMode && noBrushSelected) {
    query.ignoreInterval = true;
  }

  yield put({
    type: types.FETCHING_DIMENSION_DATA,
    dimensionName: action.dimensionName,
  });
  const source = axios.CancelToken.source();
  try {
    const response = yield call(
      getDimensionData,
      { queries: [query] },
      { cancelToken: source.token },
      http,
    );

    if (realTimeMode) {
      const adjustedInterval = get(
        response,
        '[0].annotations[0].value',
        `${moment().format()}/${moment().format()}`,
      );
      const [from, to] = adjustedInterval && adjustedInterval.split('/');

      if (from && to) {
        yield put(setDimensionTableHeaderInterval({ from, to }));
      }
    }

    yield put({
      type: types.FETCHING_DIMENSION_DATA_SUCCESS,
      dimensionName: action.dimensionName,
      response: get(response, '[0]'),
    });
  } catch (error) {
    yield put({
      type: types.FETCHING_DIMENSION_DATA_FAIL,
      dimensionName: action.dimensionName,
      errorCode: error.code,
      errorMsg: error.message,
    });
  } finally {
    if (yield cancelled()) {
      yield put({
        type: types.FETCHING_DIMENSION_DATA_ABORT,
        dimensionName: action.dimensionName,
      });
      source.cancel();
    }
  }
}

function* setDimensions(action) {
  const { persistedDimensions } = action;
  const defaultDimensions = yield select(dimensionNamesByMetricSelector);
  const dimensions = persistedDimensions || defaultDimensions;

  if (!Array.isArray(dimensions) || dimensions.length === 0) {
    yield put({
      type: types.SET_DIMENSIONS,
      dimensions: [],
    });
  }

  const { dimensions: currentDimensions, isOrderByTriggered } = yield select(selectors);
  const newDimensions = dimensions.reduce((prevDimensions, dimensionName, index) => {
    const mirrorPrevDimensions = prevDimensions;
    // If we fetch new data by ordering metric, don't reset the field which ends with 'ERROR',
    // because the data of this field will not change by api.
    if (isOrderByTriggered && endsWith(dimensionName, 'ERROR')) {
      mirrorPrevDimensions[dimensionName] = get(
        currentDimensions,
        [dimensionName],
        createDimension(dimensionName, index),
      );
      return mirrorPrevDimensions;
    }
    mirrorPrevDimensions[dimensionName] = createDimension(dimensionName, index);
    return mirrorPrevDimensions;
  }, {});

  yield put({
    type: types.SET_DIMENSIONS,
    dimensions: newDimensions,
  });
}

function* setDimensionSortByMetric(action) {
  const { metricName, skipPersistence } = action;
  // don't automatically set the metric
  // as there will be a persisted order by value
  if (skipPersistence) {
    return;
  }

  yield put({
    type: types.SET_DIMENSION_SORT_BY_METRIC,
    dimensionSortByMetric: METRICS_MAPPING[metricName] || metricName,
  });
}

function* setMetrics(action) {
  const { metricName } = action;
  yield put({
    type: types.SET_METRICS,
    metrics: METRICS_MAPPING[metricName] ? [METRICS_MAPPING[metricName], metricName] : [metricName],
  });
}

function* setAllQueries() {
  const dimensionListStore = yield select(dimensionListSelector);
  const dimensionTablesStore = yield select(dimensionTablesSelector);

  const dimensionList = dimensionListStore.response;
  if (dimensionList.length === 0) {
    return;
  }
  const { dimensions } = dimensionTablesStore;
  const queries = {};
  const queryTasks = Object.keys(dimensions).reduce(
    (tasks, dimensionName) => [
      ...tasks,
      setQueryByDimensionName(queries, dimensionName, dimensionList),
    ],
    [],
  );

  yield all([
    ...queryTasks,
    put({
      type: types.SET_ALL_QUERIES,
      queries,
    }),
  ]);
}

function* setQuery(action) {
  const dimensionListStore = yield select(dimensionListSelector);
  const { dimensionName } = action.target;
  const dimensionList = dimensionListStore.response;
  if (dimensionList.length === 0) {
    return;
  }
  const query = yield createQueryByDimensionName(dimensionName, dimensionList);
  yield put({
    type: types.SET_QUERY,
    dimensionName,
    query,
  });
  yield fetchingDimensionData({ dimensionName });
}

function* fetchingAllDimensionData() {
  const horizontalLayout = yield select(horizontalLayoutSelector);

  const dimensionTablesStore = yield select(dimensionTablesSelector);
  const { dimensions, isOrderByTriggered } = dimensionTablesStore;

  if (horizontalLayout || !dimensions) {
    return;
  }

  const tasks = Object.keys(dimensions)
    .filter((dimensionName) => {
      // if we change the value of 'Order by', we don't need to request the error code column again,
      // because the queries is identical
      if (isOrderByTriggered && endsWith(dimensionName, 'ERROR')) {
        return false;
      }
      return true;
    })
    .map((dimensionName) => fetchingDimensionData({ dimensionName }));

  if (isOrderByTriggered) {
    yield put({
      type: types.ORDER_BY_TRIGGER_ACTION,
      isOrderByTriggered: false,
    });
  }

  yield all(tasks);
}

function* watchSetDimensions() {
  yield takeLatest([types.SET_DIMENSIONS_FROM_DIMENSION_LIST], setDimensions);
}

function* watchSetDimensionSortByMetric() {
  yield takeLatest([metricsChartContainerTypes.SELECTED_METRIC_CHART], setDimensionSortByMetric);
}

function* watchSetMetrics() {
  yield takeLatest([metricsChartContainerTypes.SELECTED_METRIC_CHART], setMetrics);
}

function* watchSetAllQuery() {
  yield takeLatest(
    [
      commonTypes.APPLY_FILTER,
      birdChartTypes.SET_SLICE_INTERVAL,
      types.SET_DIMENSIONS,
      types.REFRESH_TABLE,
      commonTypes.INTERVAL_SELECTION,
      viewerDrillDownBirdChartTypes.SET_SLICE,
    ],
    setAllQueries,
  );
}

function* watchSetQuery() {
  yield takeLatest([types.CHANGE_DIMENSION], setQuery);
}

function* watchRefreshDimension() {
  yield takeEvery([types.REFRESH_DIMENSION], setQuery);
}

function* watchFetchAllDimensionData() {
  yield takeLatest(
    [
      types.SET_ALL_QUERIES,
      // refetch when switching to old layout
      commonTypes.TOGGLE_LAYOUT,
    ],
    fetchingAllDimensionData,
  );
}

export default [
  watchSetDimensions,
  watchSetDimensionSortByMetric,
  watchSetMetrics,
  watchSetAllQuery,
  watchFetchAllDimensionData,
  watchSetQuery,
  watchRefreshDimension,
];
